/**
 * CSV Export utilities for the Investments Widget.
 *
 * Follows the budget widget's export conventions: semicolon delimiter and
 * UTF-8 BOM for German Excel, CRLF line endings, formula-injection guard.
 */

import type { Announcement } from '../types';
import { industryLabel, stateLabel, statusLabel, t, typeLabel } from '../i18n';

/** Delimiter hint Excel reads before the header row; see downloadCSV. */
const SEPARATOR_HINT = 'sep=;\r\n';

interface CSVColumn {
  header: string;
  getValue: (a: Announcement) => string | number;
}

/**
 * Escapes a CSV value for proper handling of separators, quotes, newlines, and
 * formula-injection characters. Cells starting with =/+/-/@/\t/\r are otherwise
 * interpreted as formulas by Excel/LibreOffice; prefixing with an apostrophe
 * neutralizes that without changing what the user sees (the leading apostrophe
 * is hidden in Excel's display).
 *
 * Commas are quoted even though the delimiter is ';': an English-locale Excel
 * ignores our delimiter and splits on ',' unless the `sep=;` hint (see
 * downloadCSV) is honored, which tore rows with a comma in Standort or a
 * project name apart. Quoting makes the comma inert either way.
 */
export function escapeCSVValue(value: string | number): string {
  let stringValue = String(value);

  // Formula guard only applies to string inputs. A number like -1.5 starts
  // with '-' but isn't a formula injection vector — its type rules that out.
  if (typeof value === 'string' && stringValue.length > 0 && /^[=+\-@\t\r]/.test(stringValue)) {
    stringValue = "'" + stringValue;
  }

  // Check if value needs quoting (contains a separator, quote, or newline)
  if (/[;,"\n\r]/.test(stringValue)) {
    // Escape quotes by doubling them
    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  return stringValue;
}

// Date fields arrive as ISO *timestamps* from the API — a Postgres `date` is
// serialized as local midnight in UTC, so 2018-08-07 arrives as
// "2018-08-06T22:00:00.000Z" in CEST. Slicing that string would export the
// previous day and disagree with the date the table renders (formatDate reads
// the same value back in local time), hence the local calendar parts.
function toIsoDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Generates CSV content from investment-project announcements. Columns mirror
 * the physical table; Volumen is the raw EUR value, not the abbreviated
 * display string. Headers, federal states and industries follow the widget
 * language; numbers and dates stay machine-readable in both.
 */
export function generateInvestmentsCSV(rows: Announcement[]): string {
  const h = t().exports.csvHeaders;
  const columns: CSVColumn[] = [
    { header: h.company, getValue: a => a.investing_company ?? '' },
    { header: h.project, getValue: a => a.project_name ?? '' },
    { header: h.industry, getValue: a => (a.primary_industry ? industryLabel(a.primary_industry) : '') },
    { header: h.type, getValue: a => (a.type_of_investment ? typeLabel(a.type_of_investment) : '') },
    { header: h.volume, getValue: a => a.investment_volume_value_eur ?? '' },
    // Project-level (the API aggregates it over the cluster), matching the
    // figure the detail modal shows. Blank when no figure was curated — which
    // is not the same as zero jobs, so it must not export as 0.
    { header: h.jobs, getValue: a => a.project_jobs_created ?? a.jobs_created ?? '' },
    { header: h.firstDate, getValue: a => toIsoDate(a.first_announced_date) },
    { header: h.lastUpdate, getValue: a => toIsoDate(a.announced_event_date) },
    { header: h.projectStart, getValue: a => toIsoDate(a.project_start_date) },
    { header: h.projectEnd, getValue: a => toIsoDate(a.project_end_date) },
    {
      header: h.location,
      getValue: a => a.locations
        .map(l => `${l.city_name}${l.federal_state ? ` (${stateLabel(l.federal_state)})` : ''}`)
        .join(', '),
    },
    { header: h.status, getValue: a => (a.investment_status ? statusLabel(a.investment_status) : '') },
  ];

  const headerRow = columns.map(col => escapeCSVValue(col.header)).join(';');
  const dataRows = rows.map(row =>
    columns.map(col => escapeCSVValue(col.getValue(row))).join(';')
  );

  return [headerRow, ...dataRows].join('\r\n');
}

/**
 * Triggers a browser download of the CSV content.
 *
 * Two Excel compatibility wrappers around the raw content:
 *   - a UTF-8 BOM, so German umlauts survive the import, and
 *   - a leading `sep=;` line, which every locale's Excel reads as "this file
 *     is semicolon-delimited". Without it an English-locale Excel splits on
 *     ',' instead and scatters every cell containing a comma across extra
 *     columns. Other readers (LibreOffice, pandas, R) either honor the hint
 *     or show it as a single first row.
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Add UTF-8 BOM for Excel compatibility with German umlauts
  const BOM = '\uFEFF';
  const contentWithBOM = BOM + SEPARATOR_HINT + csvContent;

  const blob = new Blob([contentWithBOM], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Generates a filename with current date
 */
export function generateExportFilename(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return t().exports.csvFileName(`${year}-${month}-${day}`);
}
